// 🔄 통합 가격 서비스 어댑터 - 점진적 전환용
// [배경] 실제 Firebase 통합 컬렉션 전에 기존 분산 컬렉션을 통합 인터페이스로 제공
//        기존 코드 호환성을 유지하면서 점진적으로 통합 가격 시스템으로 전환

import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc,
  addDoc,
  query, 
  where,
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  UnifiedPriceDocument, 
  PriceSearchOptions, 
  generatePriceId,
  extractPureModelId,
  isCanonicalModelId,
  getConnectivityForPriceId
} from '../types/deviceCatalog';
import { DeviceGrade, GradePriceInfo } from '../types/gradeSystem';
import { IDAliasService } from './idAliasService';
// 🔑 SSOT Canonical ID 유틸 - 단일 진실 소스
import { 
  parsePriceId, 
  canonicalizeModelId, 
  buildCanonicalPriceId,
  validatePriceIdConsistency,
  inferDeviceType,
  DeviceType as CanonicalDeviceType
} from '../utils/idCanonical';

// 🔥 가격 히스토리 인터페이스
export interface PriceHistoryEntry {
  id?: string;
  priceId: string;
  modelId?: string;
  modelName?: string;
  brand?: string;
  deviceType?: string;
  changes: {
    grade: DeviceGrade;
    oldValue: number;
    newValue: number;
  }[];
  memo?: string;
  changedAt: Date | any;
  changedBy: string;
}

/**
 * 🔄 레거시 가격 컬렉션을 통합 인터페이스로 제공하는 어댑터
 */
export class UnifiedPriceAdapter {
  // 레거시 컬렉션 매핑 (웨어러블 추가)
  private static readonly LEGACY_COLLECTIONS = {
    'Apple-smartphone': 'grade_prices_apple',
    'Samsung-smartphone': 'grade_prices_samsung',
    'Apple-tablet': 'grade_prices_apple_tablet', 
    'Samsung-tablet': 'grade_prices_samsung_tablet',
    'Apple-wearable': 'grade_prices_apple_wearable', // 🎧 웨어러블 추가
    'Samsung-wearable': 'grade_prices_samsung_wearable' // Samsung 웨어러블 (향후 확장용)
  } as const;
  
  // 🔥 가격 히스토리 컬렉션
  private static readonly PRICE_HISTORY_COLLECTION = 'price_history';
  
  private static priceCache = new Map<string, any>();
  private static readonly CACHE_TTL = 2 * 60 * 1000; // 2분
  
  // 💰 단일 가격 조회 (UnifiedPriceService와 동일한 인터페이스)
  static async getPrice(
    modelId: string,
    storage: string,
    grade: DeviceGrade,
    connectivity?: string
  ): Promise<number | null> {
    const cacheKey = `price-${modelId}-${storage}-${grade}-${connectivity || ''}`;
    const cached = this.priceCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.value;
    }
    
    try {
      // modelId에서 브랜드와 타입 추출
      const { brand, deviceType } = this.parseModelId(modelId);
      if (!brand || !deviceType) return null;
      
      // 레거시 컬렉션에서 조회
      const legacyKey = `${brand}-${deviceType}` as keyof typeof this.LEGACY_COLLECTIONS;
      const collectionName = this.LEGACY_COLLECTIONS[legacyKey];
      if (!collectionName) return null;
      
      // 레거시 문서 ID 생성 (기존 로직과 호환)
      const legacyId = this.generateLegacyId(modelId, storage, connectivity);
      const docSnap = await getDoc(doc(db, collectionName, legacyId));
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const prices = data.gradePrices || data.prices;
        const price = prices?.[grade] || null;
        
        // 캐시 저장
        this.priceCache.set(cacheKey, {
          value: price,
          timestamp: Date.now()
        });
        
        return price;
      }
      
      return null;
    } catch (error) {
      console.error(`❌ 어댑터 가격 조회 실패 (${modelId}, ${grade}):`, error);
      return null;
    }
  }
  
  // 🔥 모델의 모든 등급 가격 조회 (SSOT 기반 2단계 매칭)
  // [리팩토링] 5단계 폴백 → 2단계 단순화
  // 1단계: canonical priceId로 직접 조회
  // 2단계: 오염된 modelId일 경우 정제 후 재조회
  static async getAllGradesPrice(
    modelId: string,
    storage: string,
    connectivity?: string
  ): Promise<GradePriceInfo | null> {
    // 🔑 SSOT: 오염된 modelId 정제
    const pureModelId = isCanonicalModelId(modelId) ? modelId : extractPureModelId(modelId);
    
    const cacheKey = `all-grades-${pureModelId}-${storage}-${connectivity || ''}`;
    const cached = this.priceCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.value;
    }
    
    try {
      // 1단계: canonical priceId로 직접 조회
      const canonicalPriceId = IDAliasService.createCanonicalPriceId(
        pureModelId,
        { storage, connectivity }
      );
      
      // 먼저 정확한 문서 ID로 조회 시도
      const directDocSnap = await getDoc(doc(db, 'unified_prices', canonicalPriceId));
      
      if (directDocSnap.exists()) {
        const data = directDocSnap.data();
        const prices = data.gradePrices;
        
        // 캐시 저장
        this.priceCache.set(cacheKey, {
          value: prices,
          timestamp: Date.now()
        });
        
        return prices;
      }
      
      // 2단계: 쿼리 기반 매칭 (modelId + storage + connectivity)
      
      const unifiedSnapshot = await getDocs(collection(db, 'unified_prices'));
      
      const allUnifiedPrices = unifiedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as any[];
      
      // 🔑 SSOT: price.modelId도 정제하여 비교
      const normalizeStorage = (s: string | undefined): string => {
        if (!s) return '';
        return s.toLowerCase().replace(/[^a-z0-9]/g, '');
      };
      const targetStorage = normalizeStorage(storage);
      
      const matchingPrice = allUnifiedPrices.find(price => {
        const priceModelId = isCanonicalModelId(price.modelId || '') 
          ? price.modelId 
          : extractPureModelId(price.modelId || '');
        
        const priceStorage = normalizeStorage(price.variant?.storage);
        
        // modelId 매칭 (정제된 ID로 비교)
        const modelMatch = priceModelId === pureModelId || price.modelId === modelId;
        
        // storage 매칭
        const storageMatch = priceStorage === targetStorage;
        
        // connectivity 매칭 (관대한 비교)
        const connMatch = this.compareConnectivity(price.variant?.connectivity, connectivity);
        
        return modelMatch && storageMatch && connMatch;
      });
      
      if (matchingPrice) {
        const prices = matchingPrice.gradePrices;
        
        // 캐시 저장
        this.priceCache.set(cacheKey, {
          value: prices,
          timestamp: Date.now()
        });
        
        return prices;
      }
      
      // 레거시 컬렉션 폴백 (하위호환)
      const { brand, deviceType } = this.parseModelId(pureModelId);
      if (!brand || !deviceType) {
        return null;
      }
      
      const legacyKey = `${brand}-${deviceType}` as keyof typeof this.LEGACY_COLLECTIONS;
      const collectionName = this.LEGACY_COLLECTIONS[legacyKey];
      if (!collectionName) {
        return null;
      }
      
      const legacyId = this.generateLegacyId(pureModelId, storage, connectivity);
      const docSnap = await getDoc(doc(db, collectionName, legacyId));
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const prices = data.gradePrices || data.prices;
        
        // 캐시 저장
        this.priceCache.set(cacheKey, {
          value: prices,
          timestamp: Date.now()
        });
        
        return prices;
      }
      
      return null;
    } catch (error) {
      console.error(`❌ 전체 등급 가격 조회 실패 (${modelId}):`, error);
      return null;
    }
  }
  
  // 🏢 관리자용: 브랜드+카테고리별 모든 가격 조회 (unified_prices 우선, wearable 지원)
  static async getPricesForManagement(
    brand: 'Apple' | 'Samsung' | 'LG',
    deviceType: 'smartphone' | 'tablet' | 'wearable'
  ): Promise<UnifiedPriceDocument[]> {
    const cacheKey = `mgmt-${brand}-${deviceType}`;
    const cached = this.priceCache.get(cacheKey);

    console.log(`🔍 [getPricesForManagement] ${brand} ${deviceType} - 캐시 확인:`, cached ? `캐시됨 (${cached.value.length}개)` : '캐시 없음');

    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      console.log(`✅ [getPricesForManagement] 캐시 반환: ${cached.value.length}개`);
      return cached.value;
    }
    
    try {
      // 1단계: unified_prices 컬렉션에서 우선 조회
      
      const unifiedSnapshot = await getDocs(query(
        collection(db, 'unified_prices'),
        orderBy('updatedAt', 'desc')
      ));
      
      // 전체 조회 후 클라이언트 사이드 필터링 (인덱스 에러 방지)
      const allUnifiedPrices = unifiedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as UnifiedPriceDocument[];
      
      // 브랜드와 디바이스 타입으로 필터링 (wearable 지원)
      const filteredUnified = allUnifiedPrices.filter(price =>
        price.brand === brand &&
        price.deviceType === deviceType // smartphone, tablet, wearable 모두 직접 매칭
      );

      console.log(`📊 [getPricesForManagement] 전체: ${allUnifiedPrices.length}개, 필터링 결과 (${brand} ${deviceType}): ${filteredUnified.length}개`);

      // 🔍 디버그: LG smartphone 필터링 실패 원인 파악
      if (brand === 'LG' && deviceType === 'smartphone' && filteredUnified.length === 0) {
        const lgPrices = allUnifiedPrices.filter(p => p.id?.includes('lg-smartphone'));
        console.log(`🔍 [디버그] LG 스마트폰 관련 문서 ${lgPrices.length}개 발견:`);
        lgPrices.slice(0, 3).forEach(p => {
          console.log(`   - ID: ${p.id}`);
          console.log(`     brand: "${p.brand}" (타입: ${typeof p.brand})`);
          console.log(`     deviceType: "${p.deviceType}" (타입: ${typeof p.deviceType})`);
          console.log(`     매칭 실패 이유: brand=${p.brand !== brand ? '❌' : '✅'}, deviceType=${p.deviceType !== deviceType ? '❌' : '✅'}`);
        });
      }

      if (filteredUnified.length > 0) {
        // unified_prices에서 찾았으면 사용
        this.priceCache.set(cacheKey, {
          value: filteredUnified,
          timestamp: Date.now()
        });
        
        return filteredUnified;
      }
      
      // 2단계: unified_prices에 없으면 레거시 컬렉션에서 폴백
      const legacyKey = `${brand}-${deviceType}` as keyof typeof this.LEGACY_COLLECTIONS;
      const collectionName = this.LEGACY_COLLECTIONS[legacyKey];
      if (!collectionName) {
        return [];
      }
      
      const legacySnapshot = await getDocs(query(
        collection(db, collectionName),
        orderBy('updatedAt', 'desc')
      ));
      
      const prices = legacySnapshot.docs.map(doc => {
        const data = doc.data();
        return this.convertLegacyToUnified({
          id: doc.id,
          collection: collectionName,
          data
        });
      });
      
      // 캐시 저장
      this.priceCache.set(cacheKey, {
        value: prices,
        timestamp: Date.now()
      });
      
      return prices;
      
    } catch (error) {
      console.error(`❌ 관리용 가격 조회 실패 (${brand}, ${deviceType}):`, error);
      return [];
    }
  }
  
  // 🔄 레거시 데이터를 UnifiedPriceDocument로 변환
  private static convertLegacyToUnified(legacyPrice: {
    id: string;
    collection: string;
    data: any;
  }): UnifiedPriceDocument {
    const data = legacyPrice.data;
    
    // storage 문자열에서 connectivity 분리
    const { storage, connectivity } = this.parseStorageString(data.storage);
    
    // modelId 생성 (추정)
    const modelId = this.generateModelIdFromLegacy(data.deviceModel, data.brand);
    
    // priceId 생성
    const priceId = generatePriceId(
      modelId,
      { storage, connectivity },
      'KR'
    );
    
    return {
      id: priceId,
      modelId,
      deviceType: data.deviceType === '스마트폰' ? 'smartphone' : 
                 data.deviceType === '태블릿' ? 'tablet' : 'wearable',
      brand: data.brand,
      region: 'KR',
      currency: 'KRW',
      variant: {
        storage,
        connectivity: connectivity as any
      },
      gradePrices: {
        A: data.gradePrices?.A || data.prices?.A || 0,
        B: data.gradePrices?.B || data.prices?.B || 0,
        C: data.gradePrices?.C || data.prices?.C || 0,
        D: data.gradePrices?.D || data.prices?.D || 0,
        E: data.gradePrices?.E || data.prices?.E || 0
      },
      isActive: data.isActive !== false,
      version: 'live',
      createdAt: data.createdAt || new Date(),
      updatedAt: data.updatedAt || new Date(),
      updatedBy: data.updatedBy || 'legacy-adapter',
      legacy: {
        originalCollection: legacyPrice.collection,
        originalId: legacyPrice.id,
        migratedAt: new Date()
      }
    };
  }
  
  // 🔧 유틸리티 함수들 (wearable 지원)
  private static parseModelId(modelId: string): { brand: string | null; deviceType: string | null } {
    const parts = modelId.split('-');
    if (parts.length < 2) return { brand: null, deviceType: null };
    
    const brand = parts[0] === 'apple' ? 'Apple' : 
                 parts[0] === 'samsung' ? 'Samsung' : null;
    const deviceType = parts[1] === 'smartphone' ? 'smartphone' :
                      parts[1] === 'tablet' ? 'tablet' : 
                      parts[1] === 'wearable' ? 'wearable' : null;
    
    return { brand, deviceType };
  }
  
  private static generateLegacyId(modelId: string, storage: string, connectivity?: string): string {
    // 기존 레거시 ID 패턴과 매칭하도록 변환
    const parts = modelId.split('-');
    if (parts.length < 3) return modelId;
    
    const brand = parts[0];
    const deviceType = parts[1] === 'smartphone' ? '스마트폰' : 
                      parts[1] === 'tablet' ? '태블릿' : '웨어러블';
    const modelParts = parts.slice(2).join('_');
    
    const storageKey = connectivity ? `${storage}_-_${connectivity}` : storage;
    return `${brand}_${deviceType}_${modelParts}_${storageKey}`.toLowerCase();
  }
  
  private static parseStorageString(storageStr: string): { storage: string; connectivity?: string } {
    if (!storageStr) return { storage: '기본' };
    
    const parts = storageStr.split(' - ').map(p => p.trim());
    
    if (parts.length >= 2) {
      const [storage, connectivity] = parts;
      return { storage, connectivity };
    }
    
    return { storage: storageStr };
  }
  
  private static generateModelIdFromLegacy(deviceModel: string, brand: string): string {
    const normalized = deviceModel.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9\-가-힣]/g, '');
    
    return `${brand.toLowerCase()}-smartphone-${normalized}`;
  }
  
  // 🔥 가격 업데이트 (관리자용) - 강화된 디버깅 + 히스토리 저장
  // 🔑 P0-4: 저장 전 검증 로직 추가 - SSOT priceId/modelId 일관성 검증
  static async updatePrice(editRequest: {
    priceId: string;
    gradePrices: GradePriceInfo;
    memo?: string;
  }, adminId: string): Promise<void> {
    try {
      // 🔑 P0-4: SSOT priceId 검증 (저장 전 필수)
      let parsedInfo;
      try {
        parsedInfo = parsePriceId(editRequest.priceId);
        
        // 🔑 디바이스 타입별 규칙 검증
        const deviceType = parsedInfo.deviceType;
        
        if (deviceType === 'smartphone' && parsedInfo.connectivity !== 'lte') {
          // 자동 보정: canonical priceId로 재생성
          const correctedPriceId = buildCanonicalPriceId(
            parsedInfo.canonicalModelId,
            'smartphone',
            { storage: parsedInfo.storage, connectivity: 'lte' },
            parsedInfo.region
          );
          editRequest.priceId = correctedPriceId;
          parsedInfo = parsePriceId(correctedPriceId);
        }
      } catch (parseError) {
        // 파싱 실패해도 기존 로직으로 계속 진행 (레거시 호환)
      }
      
      // 기존 문서 존재 여부 확인 (히스토리용)
      const existingDoc = await getDoc(doc(db, 'unified_prices', editRequest.priceId));
      
      let oldPrices: GradePriceInfo = { A: 0, B: 0, C: 0, D: 0, E: 0 };
      let existingData: any = null;

      if (existingDoc.exists()) {
        existingData = existingDoc.data();
        oldPrices = existingData?.gradePrices || { A: 0, B: 0, C: 0, D: 0, E: 0 };
      }
      
      // 🔥 변경된 등급 확인 (히스토리용)
      const changes: { grade: DeviceGrade; oldValue: number; newValue: number }[] = [];
      const grades: DeviceGrade[] = ['A', 'B', 'C', 'D', 'E'];

      grades.forEach(grade => {
        const oldValue = oldPrices[grade] || 0;
        const newValue = editRequest.gradePrices[grade] || 0;
        if (oldValue !== newValue) {
          changes.push({ grade, oldValue, newValue });
        }
      });

      // 🔥 완전한 unified_prices 문서 구조로 저장
      let priceDoc: any = {
        id: editRequest.priceId,
        gradePrices: editRequest.gradePrices,
        updatedAt: new Date(),
        updatedBy: adminId,
        version: 'live' as const,
        isActive: true,
        ...(editRequest.memo && { memo: editRequest.memo })
      };
      
      // 🔥 priceId에서 brand/deviceType 추출 (버그 수정: 올바른 순서로 파싱)
      // priceId 형식: deviceType-brand-model-...-storage-region
      // 예시: smartphone-apple-iphone-14-iphone-14-pro-128gb-kr
      const { 
        brand: extractedBrand, 
        deviceType: extractedDeviceType,
        modelId: rawExtractedModelId,
        storage: extractedStorage,
        connectivity: rawExtractedConnectivity
      } = this.extractInfoFromPriceId(editRequest.priceId);

      // 🔍 디버그: priceId에서 추출된 정보 로그
      console.log(`🔍 [updatePrice] priceId 파싱: "${editRequest.priceId}"`);
      console.log(`   ├─ brand: "${extractedBrand}"`);
      console.log(`   ├─ deviceType: "${extractedDeviceType}"`);
      console.log(`   └─ modelId: "${rawExtractedModelId}"`);

      // 🔑 P0: extractedModelId를 canonical로 정규화 (중복 생성 방지 핵심!)
      const extractedModelId = canonicalizeModelId(rawExtractedModelId);
      
      // 🔑 P0: 스마트폰 connectivity는 항상 'LTE' 강제
      const extractedConnectivity = extractedDeviceType === 'smartphone' 
        ? 'LTE' 
        : rawExtractedConnectivity;
      
      // 기존 문서가 있으면 기존 구조 유지하며 가격만 업데이트
      if (existingDoc.exists()) {
        const existingData = existingDoc.data();
        
        // 🔥 기존 데이터 보정 필요 여부 확인 (brand, deviceType, modelId, variant)
        const needsBrandFix = existingData?.brand !== extractedBrand;
        const needsTypeFix = existingData?.deviceType !== extractedDeviceType;
        const needsModelIdFix = existingData?.modelId !== extractedModelId;
        const needsStorageFix = existingData?.variant?.storage !== extractedStorage;

        // 🔍 디버그: brand 필드 보정 확인
        if (needsBrandFix) {
          console.log(`🔧 [updatePrice] brand 필드 보정: "${existingData?.brand}" → "${extractedBrand}" (priceId: ${editRequest.priceId})`);
        }

        priceDoc = {
          ...existingData, // 기존 데이터 유지
          // 🔥 핵심 필드는 priceId 기준으로 강제 보정 (매칭을 위해 필수!)
          brand: extractedBrand,
          deviceType: extractedDeviceType,
          modelId: extractedModelId,  // 🔥 modelId도 보정! (조회 시 매칭에 사용됨)
          variant: {
            ...existingData?.variant,  // 🔥 버그 수정: 기존 variant 데이터 보존! (connectivity 등)
            storage: extractedStorage,  // 🔥 storage는 priceId 기준으로 보정
            // 🔥 connectivity는 priceId에서 추출된 값이 있으면 사용, 없으면 기존 값 유지
            ...(extractedConnectivity && extractedConnectivity !== 'Wi-Fi' 
                ? { connectivity: extractedConnectivity } 
                : existingData?.variant?.connectivity 
                  ? { connectivity: existingData.variant.connectivity }
                  : {})
          },
          gradePrices: editRequest.gradePrices, // 가격만 업데이트
          updatedAt: new Date(),
          updatedBy: adminId,
          ...(editRequest.memo && { memo: editRequest.memo })
        };
      } else {
        // 새 문서인 경우 priceId에서 추출한 정보로 완전한 문서 생성
        priceDoc = {
          id: editRequest.priceId,
          modelId: extractedModelId,
          deviceType: extractedDeviceType,
          brand: extractedBrand,
          region: 'KR',
          currency: 'KRW',
          variant: {
            storage: extractedStorage,
            ...(extractedConnectivity && extractedConnectivity !== 'Wi-Fi' && { connectivity: extractedConnectivity })
          },
          gradePrices: editRequest.gradePrices,
          isActive: true,
          version: 'live' as const,
          createdAt: new Date(),
          updatedAt: new Date(),
          updatedBy: adminId,
          ...(editRequest.memo && { memo: editRequest.memo })
        };
      }
      
      // 저장 (merge: false로 완전히 덮어쓰기)
      await setDoc(doc(db, 'unified_prices', editRequest.priceId), priceDoc, { merge: false });
      
      // 캐시 무효화
      this.priceCache.clear();
      
      // 🔥 변경사항이 있으면 히스토리 저장
      if (changes.length > 0) {
        try {
          await this.savePriceHistory({
            priceId: editRequest.priceId,
            modelId: existingData?.modelId || '',
            modelName: existingData?.modelName || '',
            brand: existingData?.brand || '',
            deviceType: existingData?.deviceType || '',
            changes,
            memo: editRequest.memo || '',
            changedAt: new Date(),
            changedBy: adminId
          });
        } catch (historyError) {
          // 히스토리 저장 실패해도 가격 업데이트는 성공으로 처리
        }
      }
      
    } catch (error) {
      console.error(`❌ 가격 업데이트 실패 (${editRequest.priceId}):`, error);
      throw new Error(`가격 업데이트 실패: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  
  // 📝 가격 히스토리 저장
  static async savePriceHistory(entry: PriceHistoryEntry): Promise<void> {
    try {
      await addDoc(collection(db, this.PRICE_HISTORY_COLLECTION), {
        priceId: entry.priceId,
        modelId: entry.modelId || '',
        modelName: entry.modelName || '',
        brand: entry.brand || '',
        deviceType: entry.deviceType || '',
        changes: entry.changes,
        memo: entry.memo || '',
        changedAt: serverTimestamp(),
        changedBy: entry.changedBy
      });
    } catch (error) {
      console.error(`❌ 가격 히스토리 저장 실패:`, error);
      throw error;
    }
  }
  
  // 📊 가격 히스토리 조회 (priceId 기준)
  static async getPriceHistory(priceId: string): Promise<PriceHistoryEntry[]> {
    try {
      
      // 🔥 인덱스 없이도 동작하도록 전체 조회 후 필터링
      const snapshot = await getDocs(collection(db, this.PRICE_HISTORY_COLLECTION));
      
      const history: PriceHistoryEntry[] = [];
      
      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.priceId === priceId) {
          history.push({
            id: doc.id,
            priceId: data.priceId,
            modelId: data.modelId,
            modelName: data.modelName,
            brand: data.brand,
            deviceType: data.deviceType,
            changes: data.changes || [],
            memo: data.memo,
            changedAt: data.changedAt?.toDate?.() || (data.changedAt ? new Date(data.changedAt) : new Date()),
            changedBy: data.changedBy
          });
        }
      });
      
      // 날짜 기준 내림차순 정렬 (최신순)
      history.sort((a, b) => {
        const aTime = a.changedAt instanceof Date ? a.changedAt.getTime() : 0;
        const bTime = b.changedAt instanceof Date ? b.changedAt.getTime() : 0;
        return bTime - aTime;
      });
      
      return history;
    } catch (error) {
      console.error(`❌ 가격 히스토리 조회 실패 (${priceId}):`, error);
      return [];
    }
  }

  // 📊 통계 조회 (Mock)
  static async getStatistics() {
    try {
      const [appleSmartphone, samsungSmartphone, appleTablet, samsungTablet] = await Promise.all([
        this.getPricesForManagement('Apple', 'smartphone'),
        this.getPricesForManagement('Samsung', 'smartphone'),  
        this.getPricesForManagement('Apple', 'tablet'),
        this.getPricesForManagement('Samsung', 'tablet')
      ]);
      
      const allPrices = [...appleSmartphone, ...samsungSmartphone, ...appleTablet, ...samsungTablet];
      
      // 등급별 평균 가격 계산
      const grades: DeviceGrade[] = ['A', 'B', 'C', 'D', 'E'];
      const averagePriceByGrade: GradePriceInfo = { A: 0, B: 0, C: 0, D: 0, E: 0 };
      const priceRangeByGrade: Record<DeviceGrade, { min: number; max: number }> = {
        A: { min: 0, max: 0 },
        B: { min: 0, max: 0 },
        C: { min: 0, max: 0 },
        D: { min: 0, max: 0 },
        E: { min: 0, max: 0 }
      };
      
      grades.forEach(grade => {
        const gradePrices = allPrices.map(p => p.gradePrices[grade]).filter(p => p > 0);
        if (gradePrices.length > 0) {
          averagePriceByGrade[grade] = Math.round(
            gradePrices.reduce((sum, price) => sum + price, 0) / gradePrices.length
          );
          priceRangeByGrade[grade] = {
            min: Math.min(...gradePrices),
            max: Math.max(...gradePrices)
          };
        }
      });
      
      return {
        totalPrices: allPrices.length,
        averagePriceByGrade,
        priceRangeByGrade,
        lastUpdated: new Date()
      };
    } catch (error) {
      console.error('❌ 어댑터 통계 조회 실패:', error);
      throw new Error('통계 조회 중 오류 발생');
    }
  }
  
  // 🔥 priceId에서 brand/deviceType/modelId/storage/connectivity 추출 (버그 수정 완료)
  // 
  // ⚠️ priceId 형식은 두 가지가 혼재:
  //   1. 신규 형식 (generateModelId 기준): brand-deviceType-series-model-storage-region
  //      예시: apple-wearable-airpods-airpods-pro-1-kr
  //      예시: apple-smartphone-iphone-iphone-14-pro-128gb-kr
  //   2. 레거시 형식: deviceType-brand-model-...-storage-region  
  //      예시: smartphone-apple-iphone-14-128gb-kr
  //
  // 🔍 판별 로직: idParts[0]이 deviceType 키워드면 레거시, 아니면 신규 형식
  private static extractInfoFromPriceId(priceId: string): {
    brand: 'Apple' | 'Samsung' | 'LG';
    deviceType: 'smartphone' | 'tablet' | 'wearable';
    modelId: string;
    storage: string;
    connectivity?: string;
  } {
    // 빈 문자열 요소 필터링 (한글 storage '기본' 등으로 인한 -- 처리)
    const idParts = priceId.split('-').filter(part => part !== '');

    // 🔥 deviceType 키워드 목록
    const deviceTypeKeywords = ['smartphone', 'tablet', 'wearable'];
    // 🔥 brand 키워드 목록
    const brandKeywords = ['apple', 'samsung', 'lg'];

    let brand: 'Apple' | 'Samsung' | 'LG';
    let deviceType: 'smartphone' | 'tablet' | 'wearable';
    
    // 🔥 형식 판별: idParts[0]이 deviceType이면 레거시, brand이면 신규
    const firstPartLower = idParts[0]?.toLowerCase();
    const secondPartLower = idParts[1]?.toLowerCase();
    
    // 🔥 priceId 전체 문자열에서 deviceType 추론하는 헬퍼 함수
    const inferDeviceTypeFromPriceId = (priceIdLower: string): 'smartphone' | 'tablet' | 'wearable' => {
      // 1) 명시적 deviceType 키워드 확인
      if (priceIdLower.includes('smartphone')) return 'smartphone';
      if (priceIdLower.includes('tablet')) return 'tablet';
      if (priceIdLower.includes('wearable')) return 'wearable';
      
      // 2) 모델명 키워드로 추론
      // 태블릿: ipad, galaxy-tab, tab-s, tab-a 등
      if (priceIdLower.includes('ipad') || priceIdLower.includes('galaxy-tab') || 
          priceIdLower.match(/tab-[sa]\d/) || priceIdLower.includes('tab-')) {
        return 'tablet';
      }
      
      // 스마트폰: iphone, galaxy-s, galaxy-z, galaxy-a, galaxy-note 등
      if (priceIdLower.includes('iphone') || priceIdLower.includes('galaxy-s') || 
          priceIdLower.includes('galaxy-z') || priceIdLower.includes('galaxy-a') ||
          priceIdLower.includes('galaxy-note')) {
        return 'smartphone';
      }
      
      // 웨어러블: airpods, watch, buds 등
      if (priceIdLower.includes('airpods') || priceIdLower.includes('watch') || 
          priceIdLower.includes('buds')) {
        return 'wearable';
      }
      
      // 기본값: smartphone (태블릿/웨어러블보다 일반적)
      return 'smartphone';
    };
    
    if (deviceTypeKeywords.includes(firstPartLower)) {
      // 레거시 형식: deviceType-brand-...
      deviceType = firstPartLower as 'smartphone' | 'tablet' | 'wearable';
      brand = secondPartLower === 'apple' ? 'Apple' : secondPartLower === 'samsung' ? 'Samsung' : 'LG';
    } else if (brandKeywords.includes(firstPartLower)) {
      // 신규 형식: brand-deviceType-...
      brand = firstPartLower === 'apple' ? 'Apple' : firstPartLower === 'samsung' ? 'Samsung' : 'LG';

      // 🔥 secondPart가 deviceType이면 사용, 아니면 priceId 전체에서 추론
      if (deviceTypeKeywords.includes(secondPartLower)) {
        deviceType = secondPartLower as 'smartphone' | 'tablet' | 'wearable';
      } else {
        // priceId 전체에서 deviceType 추론 (ipad → tablet, iphone → smartphone 등)
        deviceType = inferDeviceTypeFromPriceId(priceId.toLowerCase());
      }
    } else {
      // 알 수 없는 형식 - priceId 전체에서 추론
      brand = priceId.toLowerCase().includes('apple') ? 'Apple' : priceId.toLowerCase().includes('samsung') ? 'Samsung' : 'LG';
      deviceType = inferDeviceTypeFromPriceId(priceId.toLowerCase());
    }
    
    // 마지막 'kr' 제거하고 나머지 부분에서 정보 추출
    const withoutRegion = idParts.slice(0, -1); // 마지막 'kr' 제거
    
    // 스토리지와 연결성 추출
    let storage = '128GB'; // 기본값
    let connectivity: string | undefined = undefined;
    let modelId = withoutRegion.join('-');
    
    // 스토리지 패턴 찾기 (gb, tb 등)
    const storageIndex = withoutRegion.findIndex(part =>
      /^\d+(gb|tb)$/i.test(part) ||
      part.match(/^(32|64|128|256|512|1tb|2tb)$/i)
    );

    if (storageIndex >= 0) {
      storage = withoutRegion[storageIndex].toUpperCase();

      // 연결성 확인 (스토리지 다음 요소들)
      const afterStorage = withoutRegion.slice(storageIndex + 1);
      if (afterStorage.length > 0) {
        const connectivityPart = afterStorage.join(' ').toLowerCase();
        if (connectivityPart.includes('cellular') || connectivityPart.includes('wificellular') ||
            connectivityPart.includes('lte') || connectivityPart.includes('5g')) {
          connectivity = 'Wi-Fi + Cellular';
        }
      }

      // modelId는 스토리지 이전까지
      modelId = withoutRegion.slice(0, storageIndex).join('-');
    } else if (deviceType === 'wearable') {
      // 🎧 웨어러블: 한글 구성명(풀세트 등)을 storage로 파싱
      const koreanIndex = withoutRegion.findIndex(part => /[가-힣]/.test(part));
      if (koreanIndex >= 0) {
        storage = withoutRegion[koreanIndex];
        modelId = withoutRegion.slice(0, koreanIndex).join('-');
      } else {
        storage = '기본';
      }
    }
    
    return { brand, deviceType, modelId, storage, connectivity };
  }
  
  // 🔧 connectivity 비교 유틸리티 (관대한 매칭)
  private static compareConnectivity(stored: string | undefined, requested: string | undefined): boolean {
    // 둘 다 Wi-Fi 또는 undefined인 경우
    if ((!stored || stored === 'Wi-Fi') && (!requested || requested === 'Wi-Fi')) {
      return true;
    }
    
    // 정확히 일치하는 경우
    if (stored === requested) {
      return true;
    }
    
    // 대소문자 무시하고 비교
    const storedNorm = (stored || 'Wi-Fi').toLowerCase().replace(/\s+/g, '');
    const requestedNorm = (requested || 'Wi-Fi').toLowerCase().replace(/\s+/g, '');
    
    if (storedNorm === requestedNorm) {
      return true;
    }
    
    // 셀룰러 관련 별칭들
    const cellularAliases = ['cellular', 'lte', '5g'];
    const storedIsCellular = cellularAliases.some(alias => storedNorm.includes(alias));
    const requestedIsCellular = cellularAliases.some(alias => requestedNorm.includes(alias));
    
    if (storedIsCellular && requestedIsCellular) {
      return true;
    }
    
    return false;
  }

  // 🔄 캐시 관리
  static clearCache(): void {
    this.priceCache.clear();
  }
  
  static getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.priceCache.size,
      keys: Array.from(this.priceCache.keys())
    };
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // 🔥 Phase 1: 가격 안정화 메서드들 (UnifiedPriceService와 호환)
  // ═══════════════════════════════════════════════════════════════════════════
  
  /**
   * 🔍 priceId 존재 여부 확인
   */
  static async checkPriceExists(priceId: string): Promise<boolean> {
    try {
      const docSnap = await getDoc(doc(db, 'unified_prices', priceId));
      return docSnap.exists();
    } catch (error) {
      console.error(`❌ priceId 존재 확인 실패 (${priceId}):`, error);
      return false;
    }
  }
  
  /**
   * 🆕 새 가격 문서 생성 (기존 문서 덮어쓰기 방지)
   * 🔑 P0-4: SSOT 검증 추가 - canonical priceId만 생성 허용
   */
  static async createPrice(
    priceId: string,
    modelId: string,
    brand: 'Apple' | 'Samsung' | 'LG',
    deviceType: 'smartphone' | 'tablet' | 'wearable',
    storage: string,
    connectivity?: string,
    initialPrices?: GradePriceInfo,
    adminId: string = 'system'
  ): Promise<void> {
    try {
      // 🔑 P0-4: SSOT priceId 검증 (생성 전 필수)
      let canonicalPriceId = priceId;
      let canonicalModelId = modelId;
      let canonicalConnectivity = connectivity;
      
      try {
        // modelId 정규화
        canonicalModelId = canonicalizeModelId(modelId);
        
        // 디바이스 타입별 규칙 적용
        if (deviceType === 'smartphone') {
          // 스마트폰: connectivity는 항상 'LTE' 강제
          canonicalConnectivity = 'LTE';
        } else if (deviceType === 'wearable') {
          // 웨어러블: storage/connectivity 없음
          canonicalConnectivity = undefined;
        }
        
        // canonical priceId 재생성
        canonicalPriceId = buildCanonicalPriceId(
          canonicalModelId,
          deviceType as CanonicalDeviceType,
          { 
            storage: storage, 
            connectivity: canonicalConnectivity 
          },
          'kr'
        );
        
        if (canonicalPriceId !== priceId.toLowerCase()) {
          priceId = canonicalPriceId;
        }
      } catch (canonicalError) {
        // canonical 생성 실패 시 원본 사용
      }
      
      // 🔥 버그 수정: 기존 문서 확인 및 처리
      const existingDoc = await getDoc(doc(db, 'unified_prices', priceId));

      // Firebase setDoc은 undefined 값을 허용하지 않으므로 제거
      const variant: Record<string, string> = {};
      if (storage) variant.storage = storage;
      if (canonicalConnectivity) variant.connectivity = canonicalConnectivity;

      const newDoc = {
        id: priceId,
        modelId: canonicalModelId, // 🔑 canonical modelId 사용
        brand,
        deviceType,
        region: 'KR',
        currency: 'KRW',
        variant,
        gradePrices: initialPrices || { A: 0, B: 0, C: 0, D: 0, E: 0 },
        isActive: true,
        version: 'live',
        createdAt: existingDoc.exists() ? existingDoc.data()?.createdAt : serverTimestamp(), // 기존 생성일 유지
        updatedAt: serverTimestamp(),
        updatedBy: adminId
      };

      if (existingDoc.exists()) {
        const existingData = existingDoc.data();
        // 🔍 디버그: brand 필드 업데이트 확인
        if (existingData?.brand !== brand) {
          console.log(`🔧 [createPrice] brand 필드 업데이트: "${existingData?.brand}" → "${brand}" (priceId: ${priceId})`);
        }
      }

      // ✅ 기존 문서가 있어도 덮어쓰기 (merge 없이 완전 교체)
      await setDoc(doc(db, 'unified_prices', priceId), newDoc);
      
      // 캐시 무효화
      this.priceCache.clear();
      
    } catch (error) {
      console.error(`❌ 가격 문서 생성 실패 (${priceId}):`, error);
      throw new Error(`가격 문서 생성 실패: ${priceId}`);
    }
  }
  
  /**
   * 🔥 priceId가 없으면 자동 생성 (핵심 안정화 메서드)
   * 🔑 P0: canonical priceId를 반환하여 일관성 보장
   * @returns { existed: boolean; canonicalPriceId: string }
   */
  static async ensurePriceExists(
    priceId: string,
    modelData: {
      modelId: string;
      brand: 'Apple' | 'Samsung' | 'LG';
      deviceType: 'smartphone' | 'tablet' | 'wearable';
      storage: string;
      connectivity?: string;
    },
    adminId: string = 'system'
  ): Promise<{ existed: boolean; canonicalPriceId: string }> {
    try {
      // 🔥 [디버그 로그] 
      console.log(`\n🔍 [ensurePriceExists] 문서 확인 시작`);
      console.log(`   ├─ 입력 priceId: "${priceId}"`);
      console.log(`   └─ 입력 modelId: "${modelData.modelId}"`);
      
      // 🔑 P0: 먼저 canonical priceId로 정규화
      let canonicalPriceId = priceId;
      let canonicalModelId = modelData.modelId;
      try {
        canonicalModelId = canonicalizeModelId(modelData.modelId);
        let canonicalConnectivity = modelData.connectivity;
        
        // 디바이스 타입별 규칙 적용
        if (modelData.deviceType === 'smartphone') {
          canonicalConnectivity = 'LTE'; // 스마트폰은 항상 LTE
        } else if (modelData.deviceType === 'wearable') {
          canonicalConnectivity = undefined; // 웨어러블은 connectivity 없음
        }
        
        canonicalPriceId = buildCanonicalPriceId(
          canonicalModelId,
          modelData.deviceType as CanonicalDeviceType,
          { 
            storage: modelData.storage,
            connectivity: canonicalConnectivity 
          },
          'kr'
        );
        
        // 🔥 [디버그 로그] canonical 변환 결과
        console.log(`🔍 [ensurePriceExists] canonical 변환:`);
        console.log(`   ├─ canonical modelId: "${canonicalModelId}"`);
        console.log(`   ├─ canonical priceId: "${canonicalPriceId}"`);
        console.log(`   └─ 변환됨: ${priceId !== canonicalPriceId ? '⚠️ 예 (주의!)' : '아니오'}`);
        
      } catch (canonicalError) {
        console.warn(`   ⚠️ canonical 변환 실패, 원본 사용:`, canonicalError);
        canonicalPriceId = priceId.toLowerCase();
      }
      
      // canonical priceId로 존재 여부 확인
      const exists = await this.checkPriceExists(canonicalPriceId);
      console.log(`🔍 [ensurePriceExists] canonical 문서 존재: ${exists ? '✅ 예' : '❌ 아니오'} ("${canonicalPriceId}")`);
      
      if (exists) {
        return { existed: true, canonicalPriceId };
      }
      
      // 원본 priceId로도 확인 (레거시 문서 호환)
      if (canonicalPriceId !== priceId.toLowerCase()) {
        const legacyExists = await this.checkPriceExists(priceId);
        console.log(`🔍 [ensurePriceExists] 레거시 문서 존재: ${legacyExists ? '✅ 예' : '❌ 아니오'} ("${priceId}")`);
        
        if (legacyExists) {
          console.log(`   ⚠️ 레거시 문서 사용 (기존 문서 업데이트용)`);
          return { existed: true, canonicalPriceId: priceId }; // 레거시 ID 반환 (기존 문서 업데이트용)
        }
      }
      
      // 🔥 [디버그 로그] 신규 문서 생성
      console.log(`🔍 [ensurePriceExists] 🆕 신규 문서 생성: "${canonicalPriceId}"`);
      
      await this.createPrice(
        canonicalPriceId, // 🔑 canonical priceId로 생성!
        modelData.modelId,
        modelData.brand,
        modelData.deviceType,
        modelData.storage,
        modelData.connectivity,
        undefined,
        adminId
      );
      
      return { existed: false, canonicalPriceId };
    } catch (error) {
      console.error(`❌ ensurePriceExists 실패 (${priceId}):`, error);
      throw error;
    }
  }
  
  /**
   * 🔥 안전한 가격 업데이트 (존재 확인 후 업데이트, 없으면 생성)
   * 🔑 P0: canonical priceId로 일관되게 처리하여 중복 생성 방지
   */
  static async safeUpdatePrice(
    editRequest: { priceId: string; gradePrices: GradePriceInfo; memo?: string },
    modelData: {
      modelId: string;
      brand: 'Apple' | 'Samsung' | 'LG';
      deviceType: 'smartphone' | 'tablet' | 'wearable';
      storage: string;
      connectivity?: string;
    },
    adminId: string
  ): Promise<{ created: boolean; updated: boolean; canonicalPriceId: string }> {
    const result = { created: false, updated: false, canonicalPriceId: editRequest.priceId };
    
    // 🔥 [디버그 로그] safeUpdatePrice 진입
    console.log(`\n🔧 [safeUpdatePrice] 요청 처리 시작`);
    console.log(`   ├─ 요청 priceId: "${editRequest.priceId}"`);
    console.log(`   ├─ modelId: "${modelData.modelId}"`);
    console.log(`   ├─ storage: "${modelData.storage}"`);
    console.log(`   └─ connectivity: "${modelData.connectivity}"`);
    
    try {
      // 🔑 P0 핵심 수정: ensurePriceExists가 반환한 canonicalPriceId를 사용!
      const ensureResult = await this.ensurePriceExists(editRequest.priceId, modelData, adminId);
      result.created = !ensureResult.existed;
      result.canonicalPriceId = ensureResult.canonicalPriceId;
      
      // 🔥 [디버그 로그] ensurePriceExists 결과
      console.log(`🔧 [safeUpdatePrice] ensurePriceExists 결과:`);
      console.log(`   ├─ 문서 존재: ${ensureResult.existed ? '예' : '아니오 (신규 생성)'}`);
      console.log(`   ├─ 최종 priceId: "${ensureResult.canonicalPriceId}"`);
      console.log(`   └─ priceId 변환됨: ${editRequest.priceId !== ensureResult.canonicalPriceId ? '⚠️ 예' : '아니오'}`);
      
      // 🔑 P0 핵심 수정: canonical priceId로 업데이트 요청 수정
      const canonicalEditRequest = {
        ...editRequest,
        priceId: ensureResult.canonicalPriceId // 🔑 여기가 핵심! canonical priceId로 교체
      };
      
      
      // 2단계: canonical priceId로 가격 업데이트
      await this.updatePrice(canonicalEditRequest, adminId);
      result.updated = true;
      
      console.log(`🔧 [safeUpdatePrice] 업데이트 완료 → "${ensureResult.canonicalPriceId}"`);
      
      return result;
      
    } catch (error) {
      console.error(`❌ safeUpdatePrice 실패 (${editRequest.priceId}):`, error);
      throw error;
    }
  }
}

// 🎯 환경별 서비스 선택 (개발 시에는 어댑터, 프로덕션에서는 실제 통합 서비스)
export const getUnifiedPriceService = () => {
  // 🔥 Vite 환경 변수 사용 (process 대신 import.meta.env)
  const nodeEnv = import.meta.env.MODE || 'development';
  const useUnifiedEnv = import.meta.env.VITE_USE_UNIFIED_PRICES || 'false';
  const useUnifiedCollection = nodeEnv === 'production' && useUnifiedEnv === 'true';
  
  if (useUnifiedCollection) {
    // 실제 통합 서비스 사용 (향후)
    const { UnifiedPriceService } = require('./unifiedPriceService');
    return UnifiedPriceService;
  } else {
    // 어댑터 사용 (현재)
    return UnifiedPriceAdapter;
  }
};
